import React, { useEffect, useState } from 'react';
import { history } from 'umi';
import {
  Button,
  Card,
  Form,
  InputNumber,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { FolderOpenOutlined, ReloadOutlined } from '@ant-design/icons';
import { getMemberTrainingWorkspaceMemberPage } from '@/services/technical';
import {
  succeeded,
  timeText,
  writeWorkspaceQueryState,
} from './SimpleWorkspaceSupport';
import styles from './SimpleWorkspace.less';

const { Paragraph, Title } = Typography;
const { Option } = Select;
const PAGE_SIZE = 20;

const SOURCE_LABELS = {
  action_observation: '动作评估',
  course_observation: '课程评估',
  capability_assessment: '运动能力',
  guided_course: 'AI 跟练',
  live_coaching: '1v1 指导课',
  questionnaire: '会员档案',
  member_feedback: '会员反馈',
  workwx_chat: '企微群聊',
  member_activity: '运动打卡',
  lifestyle_context: '生活记录',
  training_statistics: '训练统计',
};

const EVIDENCE_LABELS = {
  action_motion_observation: '动作评估更新',
  course_motion_observation: '课程评估更新',
  capability_account_snapshot: '运动能力更新',
  course_execution: '课程完成记录',
  action_training_execution: '动作训练记录',
  activity_execution: '运动打卡',
  communication_observation: '沟通事实更新',
  member_goal_change: '训练目标变化',
  member_goal_health_profile_snapshot: '会员档案更新',
  member_training_feature_snapshot: '训练特征更新',
};

const QUALITY_LABELS = {
  qualified: { label: '信息完整', color: 'green' },
  partial: { label: '部分可用', color: 'gold' },
  unqualified: { label: '不可用于判断', color: 'red' },
  unavailable: { label: '数据不可用', color: 'default' },
};

export default function MemberTrainingArchiveList() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });

  const load = async (current = 1, pageSize = PAGE_SIZE) => {
    const values = form.getFieldsValue();
    setLoading(true);
    try {
      const response = await getMemberTrainingWorkspaceMemberPage({
        requestData: {
          memberId: values.memberId || null,
          sourceType: values.sourceType || null,
        },
        page: { currentPage: current, pageSize },
      });
      if (!succeeded(response)) {
        setRecords([]);
        setPage({ current, pageSize, total: 0 });
        message.error(response?.msg || '训练档案列表查询失败');
        return;
      }
      setRecords(response.data || []);
      setPage({
        current,
        pageSize,
        total: response.page?.count || 0,
      });
    } catch (error) {
      setRecords([]);
      setPage({ current, pageSize, total: 0 });
      message.error(error?.message || '训练档案列表暂时不可用');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openArchive = (record) => {
    writeWorkspaceQueryState({
      memberId: record.memberId,
      phone: '',
      activeTab: 'overview',
    });
    history.push({
      pathname: '/technical/memberTrainingWorkspace',
      query: { view: 'detail', memberId: record.memberId },
    });
  };

  const columns = [
    {
      title: '会员 ID',
      dataIndex: 'memberId',
      width: 160,
    },
    {
      title: '最近变化',
      dataIndex: 'latestEvidenceType',
      render: (value) => EVIDENCE_LABELS[value] || value || '-',
    },
    {
      title: '数据来源',
      dataIndex: 'latestSourceType',
      render: (value) => SOURCE_LABELS[value] || value || '-',
    },
    {
      title: '数据状态',
      dataIndex: 'latestQualityStatus',
      width: 120,
      render: (value) => {
        const quality = QUALITY_LABELS[value] || {
          label: value || '-',
          color: 'default',
        };
        return <Tag color={quality.color}>{quality.label}</Tag>;
      },
    },
    {
      title: '累计证据',
      dataIndex: 'evidenceCount',
      width: 110,
      render: (value) => `${Number(value) || 0} 条`,
    },
    {
      title: '业务发生时间',
      dataIndex: 'latestOccurredAt',
      width: 190,
      render: timeText,
    },
    {
      title: '档案更新时间',
      dataIndex: 'lastChangedAt',
      width: 190,
      render: timeText,
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<FolderOpenOutlined />}
          onClick={() => openArchive(record)}
        >
          查看档案
        </Button>
      ),
    },
  ];

  return (
    <Space
      direction="vertical"
      size={16}
      className={`${styles.workspace} ${styles.workspaceContent}`}
    >
      <Card bordered={false} className={styles.heroCard}>
        <div className={styles.heroHeading}>
          <div>
            <Title level={2} className={styles.pageTitle}>
              会员训练档案
            </Title>
            <Paragraph type="secondary" className={styles.pageDescription}>
              按训练证据最近写入时间展示发生变化的会员，进入档案后查看完整训练历程。
            </Paragraph>
          </div>
        </div>
      </Card>

      <Card bordered={false} className={styles.tabsCard}>
        <Form
          form={form}
          layout="inline"
          onFinish={() => load(1, page.pageSize)}
          className={styles.archiveFilter}
        >
          <Form.Item name="memberId" label="会员 ID">
            <InputNumber
              min={1}
              precision={0}
              controls={false}
              allowClear
              placeholder="精确查询会员"
              style={{ width: 220 }}
            />
          </Form.Item>
          <Form.Item name="sourceType" label="最近来源">
            <Select allowClear placeholder="全部来源" style={{ width: 180 }}>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              查询
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                load(1, page.pageSize);
              }}
            >
              重置
            </Button>
          </Form.Item>
        </Form>

        <Table
          rowKey="memberId"
          loading={loading}
          columns={columns}
          dataSource={records}
          scroll={{ x: 1200 }}
          pagination={{
            current: page.current,
            pageSize: page.pageSize,
            total: page.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `共 ${total} 位会员`,
            onChange: load,
          }}
          locale={{ emptyText: '暂无已形成训练档案的会员' }}
        />
      </Card>
    </Space>
  );
}
